import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiLayers } = FiIcons;

const GoalSummaryTable = ({ goals }) => {
  // Helper functions for calculations
  const calculateMetrics = (goal) => {
    const years = parseInt(goal.yearsFromNow);
    const currentCost = parseInt(goal.currentCost);
    
    // Determine inflation rate
    let inflationRate = 6.5;
    const lowerName = goal.name.toLowerCase();
    if (lowerName.includes('education')) inflationRate = 10.5;
    else if (lowerName.includes('health') || lowerName.includes('medical')) inflationRate = 9.5;

    // Future Value
    const futureValue = currentCost * Math.pow(1 + inflationRate / 100, years);

    // Expected Return
    const expectedReturn = goal.expectedReturn 
      ? parseFloat(goal.expectedReturn) 
      : (years > 7 ? 12 : years > 3 ? 10 : 7);
    
    // SIP
    const monthlyReturn = expectedReturn / 100 / 12;
    const months = years * 12;
    const sip = (monthlyReturn === 0) 
      ? futureValue / months 
      : (futureValue * monthlyReturn) / (Math.pow(1 + monthlyReturn, months) - 1);
        
    // Lumpsum
    const annualRate = expectedReturn / 100;
    const lumpsum = futureValue / Math.pow(1 + annualRate, years);

    return { futureValue, sip, lumpsum, inflationRate };
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8"
    >
      <div className="bg-gradient-to-r from-gray-50 to-white p-6 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <SafeIcon icon={FiLayers} className="text-2xl text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">Goal Financial Summary</h2>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 min-w-[200px]">Goal Name</th>
              <th className="px-6 py-4 text-center">Time Horizon</th>
              <th className="px-6 py-4 text-right">Future Value (Est.)</th>
              <th className="px-6 py-4 text-right">Required SIP</th>
              <th className="px-6 py-4 text-right">Required Lumpsum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {goals.map((goal) => {
              const { futureValue, sip, lumpsum, inflationRate } = calculateMetrics(goal);
              return (
                <tr key={goal.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-indigo-50 rounded text-indigo-600">
                        <SafeIcon icon={FiTarget} />
                      </div>
                      <span>{goal.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      parseInt(goal.yearsFromNow) <= 3 ? 'bg-red-100 text-red-800' :
                      parseInt(goal.yearsFromNow) <= 7 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {goal.yearsFromNow} Years
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-gray-700">
                    ₹{Math.round(futureValue).toLocaleString('en-IN')}
                    <div className="text-xs text-gray-400 font-normal">@{inflationRate}% infl.</div>
                  </td>
                  <td className="px-6 py-4 text-right text-indigo-600 font-bold">
                    ₹{Math.round(sip).toLocaleString('en-IN')}/mo
                  </td>
                  <td className="px-6 py-4 text-right text-green-600 font-bold">
                    ₹{Math.round(lumpsum).toLocaleString('en-IN')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default GoalSummaryTable;